import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { blogPosts } from '../data/blogData';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { trackBlogView } from '../utils/analytics';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  // Track blog view
  React.useEffect(() => {
    if (post && post.title) {
      trackBlogView(post.title);
    }
  }, [post]);

  if (!post) {
    return (
      <div>
        <Header />
        <Helmet>
          <title>Post Not Found | Right Shade</title>
        </Helmet>
        <div style={{ paddingTop: '120px', textAlign: 'center', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>
            <h1 className="heading-1" style={{ marginBottom: '16px' }}>Post Not Found</h1>
            <Link to="/blogs" className="btn-primary">Back to Blogs</Link>
          </div>
        </div>
      </div>
    );
  }

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://rightshade.in';
  const blogUrl = `${siteUrl}/blog/${post.slug}`;

  // Generate Article Schema.org structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.image,
    "author": {
      "@type": "Organization",
      "name": post.author,
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Right Shade",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": blogUrl
    },
    "keywords": post.keywords?.join(', '),
    "articleSection": post.category,
    "wordCount": post.content?.split(/\s+/).length || 2000
  };

  // Generate BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blogs",
        "item": `${siteUrl}/blogs`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": blogUrl
      }
    ]
  };

  return (
    <div>
      <Helmet>
        <title>{post.title} | Right Shade</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords?.join(', ')} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:site_name" content="Right Shade" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={blogUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={post.image} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={blogUrl} />
        
        {/* Article specific */}
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        
        {/* Schema.org Article Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        
        {/* Schema.org Breadcrumb Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <Header />
      <FixedActions />
      
      <main style={{ paddingTop: '120px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          {/* Back Button */}
          <div style={{ marginBottom: '32px' }}>
            <Link to="/blogs" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <ArrowLeft size={16} style={{ marginRight: '8px' }} />
              Back to Blogs
            </Link>
          </div>

          {/* Article Header */}
          <header style={{ marginBottom: '32px' }}>
            <div style={{ marginBottom: '16px' }}>
              <span style={{
                background: 'var(--bg-subtle)',
                color: 'var(--text-primary)',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {post.category}
              </span>
            </div>

            <h1 className="display-medium" style={{ marginBottom: '24px' }}>{post.title}</h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px', color: 'var(--text-light)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={18} />
                <span>{post.author}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} />
                <span>{new Date(post.publishDate).toLocaleDateString()}</span>
              </div>
              <div style={{ fontSize: '14px', color: '#718096' }}>
                {post.readTime}
              </div>
            </div>

            {post.keywords && post.keywords.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' }}>
                {post.keywords.slice(0, 5).map((keyword, idx) => (
                  <span key={idx} style={{
                    background: 'var(--border-light)',
                    color: 'var(--text-secondary)',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '14px'
                  }}>
                    {keyword}
                  </span>
                ))}
              </div>
            )}

            <div style={{ borderRadius: '24px', overflow: 'hidden', marginBottom: '32px' }}>
              <img 
                src={post.image} 
                alt={post.title}
                style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </header>

          {/* Article Content */}
          <article className="blog-content" style={{ lineHeight: '1.8', fontSize: '18px', color: '#2D3748' }}>
            <div 
              style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{
                __html: post.content
                  // Convert markdown links [text](url) to HTML links
                  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #D69E2E; text-decoration: underline;">$1</a>')
                  // Convert **bold** to <strong>
                  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                  // Convert headers
                  .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                  .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                  .replace(/^# (.+)$/gm, '<h1>$1</h1>')
              }}
            />
          </article>

          {/* Related Posts */}
          <section style={{ marginTop: '64px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <h3 className="heading-2" style={{ marginBottom: '24px' }}>Related Articles</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              {blogPosts.filter(p => p.slug !== post.slug && p.category === post.category).slice(0, 2).map(relatedPost => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`} className="network-card hover-lift" style={{ textDecoration: 'none', padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '16px', alignItems: 'center' }}>
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>
                        {relatedPost.title}
                      </h4>
                      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
                        {relatedPost.excerpt.substring(0, 100)}...
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <style jsx>{`
        .blog-content h1 {
          font-size: 2rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 32px 0 16px 0;
        }

        .blog-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 24px 0 12px 0;
        }

        .blog-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 20px 0 8px 0;
        }

        .blog-content p {
          margin: 16px 0;
          color: var(--text-secondary);
        }

        .blog-content ul, .blog-content ol {
          margin: 16px 0;
          padding-left: 24px;
          color: var(--text-secondary);
        }

        .blog-content li {
          margin: 8px 0;
        }

        @media (max-width: 1024px) {
          article > div:last-child {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
        
        @media (max-width: 768px) {
          main {
            padding: 100px 20px 60px 20px !important;
          }
          
          article {
            padding: 30px 20px !important;
          }
          
          article h1 {
            font-size: 1.75rem !important;
          }
          
          .blog-content {
            font-size: 15px !important;
          }
          
          .blog-content h1 {
            font-size: 1.5rem !important;
          }
          
          .blog-content h2 {
            font-size: 1.25rem !important;
          }
          
          .blog-content h3 {
            font-size: 1.1rem !important;
          }
          
          /* Related posts */
          section:last-of-type > div > div {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }
        
        @media (max-width: 480px) {
          article h1 {
            font-size: 1.5rem !important;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default BlogPost;